import { store } from '../store';
import { assemblyEditorSliceActions } from '../store/features/assemblyEditor/assemblyEditorSlice';
import { HakoBioPluginSDK } from 'hakobio-plugin-sdk';
import * as Sentry from '@sentry/react';

const JWT_MAX_AGE = 50 * 60 * 1000;
const JWT_REFRESH_TIMEOUT = 10000;
const AUTHENTICATION_ERROR_STATUSES = [401, 403];

let tokenRefreshPromise = null;

function setupUserSentry(token) {
  try {
    let base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    let parsedJwt = JSON.parse(jsonPayload);
    Sentry.setUser({
      email: parsedJwt.email,
      id: parsedJwt.id
    });
  } catch (error) {
    console.warn('Unable to configure Sentry user from JWT', error);
  }
}

function waitForJWT() {
  return new Promise((resolve, reject) => {
    let unsubscribe = () => {};
    const timeout = setTimeout(() => {
      unsubscribe();
      reject(new Error('Unable to refresh authentication token'));
    }, JWT_REFRESH_TIMEOUT);

    const checkJWT = () => {
      const jwt = store.getState().assemblyEditorSlice.jwt;
      if (jwt) {
        clearTimeout(timeout);
        unsubscribe();
        resolve(jwt);
      }
    };

    unsubscribe = store.subscribe(checkJWT);
    checkJWT();
  });
}

function clearToken() {
  store.dispatch(assemblyEditorSliceActions.setJWT({ jwt: null, jwtTime: null }));
}

async function refreshToken() {
  if (!tokenRefreshPromise) {
    clearToken();
    tokenRefreshPromise = waitForJWT();
    HakoBioPluginSDK.getJWT();
  }

  try {
    return await tokenRefreshPromise;
  } finally {
    tokenRefreshPromise = null;
  }
}

async function checkToken(forceRefresh = false) {
  let localStore = store.getState();
  const jwt = localStore.assemblyEditorSlice.jwt;
  const lastTime = parseInt(localStore.assemblyEditorSlice.jwtTime, 10);
  const isExpired = !lastTime || lastTime + JWT_MAX_AGE <= Date.now();
  if (forceRefresh || !jwt || isExpired) {
    const receivedJwt = await refreshToken();
    setupUserSentry(receivedJwt);
    return receivedJwt;
  } else {
    setupUserSentry(jwt);
    return jwt;
  }
}

/**
 *
 * @param {string} method
 * @param {any} body
 * @returns
 */
export const createAuthRequest = async (method = 'GET', body = null) => {
  const token = await checkToken();
  const mode = 'cors';
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Authorization', `Bearer ${token}`);
  if (!body) {
    return { method, mode, headers };
  } else {
    return { method, mode, headers, body: JSON.stringify(body) };
  }
};

export const createAuthRequestFile = async (method = 'GET', body = null) => {
  const token = await checkToken();
  const mode = 'cors';
  const headers = new Headers();
  //headers.append('Content-Type', 'application/json');
  headers.append('Authorization', `Bearer ${token}`);
  if (!body) {
    return { method, mode, headers };
  } else {
    const data = new FormData();
    data.append('file', body);
    return { method, mode, headers, body: data };
  }
};

/**
 * Sends an authenticated request and retries it once with a fresh JWT after a 401 or 403 response.
 */
export const authenticatedFetch = async (url, method = 'GET', body = null, isFile = false) => {
  const createRequest = isFile ? createAuthRequestFile : createAuthRequest;
  let response = await fetch(url, await createRequest(method, body));

  if (AUTHENTICATION_ERROR_STATUSES.includes(response.status)) {
    await checkToken(true);
    response = await fetch(url, await createRequest(method, body));
  }

  return response;
};
